import * as THREE from 'three';
import { commonGLSL, curvatureGLSL, lightingGLSL, terrainVisGLSL } from '../shaders/common';
import { shadowGLSL, type ShadowCaster } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';

/**
 * Billboard impostors for every tree (near records beyond the mesh distance + the whole mid-range forest),
 * drawn in ONE instanced call with all culling/LOD done in the vertex shader:
 *   - collapsed when closer than the mesh fade band (complementary dither with the mesh trees)
 *   - collapsed beyond the impostor distance
 *   - atlas view chosen from the camera azimuth around the tree (8 views, 45 deg apart)
 * Impostor layout (public/models/impostors.json), per tree: 8 cells [u0,v0,u1,v1] (v = 0 at top of image),
 * world width/height and base offset.
 */
export interface ImpostorTree { cells: number[][]; width: number; height: number; baseOffset: number }
export interface ImpostorLayout {
  views: number;
  trees: Record<string, ImpostorTree>;
}

/** Parse public/models/impostors.json (tree entries sit beside metadata keys). */
export function parseImpostorLayout(json: Record<string, unknown>): ImpostorLayout {
  const trees: Record<string, ImpostorTree> = {};
  const src = (json.trees && typeof json.trees === 'object' ? json.trees : json) as Record<string, unknown>;
  for (const [k, v] of Object.entries(src)) {
    if (v && typeof v === 'object' && 'cells' in (v as object)) trees[k] = v as ImpostorTree;
  }
  return { views: (json.views as number) ?? 8, trees };
}

const vert = /* glsl */ `
uniform sampler2D uCells;   // (views x variants) RGBA32F = u0,v0,u1,v1
uniform sampler2D uDims;    // (1 x variants) RGBA32F = width, height, baseOffset, 0
uniform float uViews;
uniform vec3 uViewPos;      // camera (or light-facing reference)
uniform vec3 uFaceDir;      // when non-zero, billboards face this direction (shadow pass)
uniform float uMinDist;
uniform float uMaxDist;
uniform float uFadeStart;
uniform float uFadeEnd;
uniform vec4 uWind;
uniform float uTime;
in vec4 aImp0; // x y z rot
in vec4 aImp1; // scale, variant, rand, 0
out vec2 vUv;
out vec3 vWorld;
out float vFade;
out float vRand;
out float vRot;
void main() {
  vec3 base = aImp0.xyz;
  float d = distance(base, uViewPos);
  float fade = 1.0 - smoothstep(uFadeStart, uFadeEnd, d); // 1 = mesh fully visible
  if (d < uMinDist || d > uMaxDist || fade >= 0.999) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  int vi = int(aImp1.y + 0.5);
  vec4 dims = texelFetch(uDims, ivec2(0, vi), 0);
  float s = aImp1.x;
  vec3 toCam = length(uFaceDir) > 0.0 ? uFaceDir : normalize(uViewPos - base);
  vec2 tc = normalize(toCam.xz + vec2(1e-5, 0.0));
  // camera->tree direction in the tree's local frame (instance rotation a: world = R(a) * local)
  vec2 dw = -tc;
  float c = cos(aImp0.w), sn = sin(aImp0.w);
  vec2 dl = vec2(c * dw.x - sn * dw.y, sn * dw.x + c * dw.y);
  float theta = atan(dl.y, -dl.x);
  int view = int(mod(floor(theta / (6.2831853 / uViews) + 0.5), uViews));
  vec4 cell = texelFetch(uCells, ivec2(view, vi), 0);
  vec3 right = vec3(tc.y, 0.0, -tc.x);
  vec2 q = position.xy; // x in [-0.5, 0.5], y in [0, 1]
  vec3 wp = base + right * q.x * dims.x * s + vec3(0.0, (q.y * dims.y - dims.z) * s, 0.0);
  // gentle sway of the crown top
  wp.xz += uWind.xy * q.y * q.y * (0.02 + 0.01 * uWind.z) * s * sin(uTime * 0.7 + aImp1.z * 6.28);
  vUv = vec2(mix(cell.x, cell.z, q.x + 0.5), mix(cell.w, cell.y, q.y));
  vWorld = wp;
  vFade = fade;
  vRand = aImp1.z;
  vRot = aImp0.w;
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const frag = /* glsl */ `
uniform sampler2D uAlbedo;
uniform sampler2D uNormal;
in vec2 vUv;
in vec3 vWorld;
in float vFade;
in float vRand;
in float vRot;
layout(location = 0) out vec4 fragColor;
void main() {
  if (vFade > hash12(floor(gl_FragCoord.xy))) discard; // complementary to the mesh dither
  vec4 a = texture(uAlbedo, vUv);
  if (a.a < 0.35) discard;
  vec3 n = texture(uNormal, vUv).xyz * 2.0 - 1.0;
  // atlas normals are world-space for an unrotated instance -> rotate by the instance yaw
  float c = cos(vRot), s = sin(vRot);
  vec3 N = normalize(vec3(c * n.x + s * n.z, n.y, -s * n.x + c * n.z));
  vec3 alb = a.rgb * (0.9 + 0.2 * vRand);
  float snow = smoothstep(mix(1.0, 0.3, uSnowCover), mix(1.0, 0.3, uSnowCover) + 0.25, N.y * 0.8 + 0.2) * step(0.001, uSnowCover);
  alb = mix(alb, vec3(0.88, 0.9, 0.93), snow * 0.8);
  vec3 V = normalize(cameraPosition - vWorld);
  float sh = getShadow(vWorld, vec3(0.0, 1.0, 0.0), gl_FragCoord.xy);
  vec3 col = shadeSurface(alb, N, V, vWorld, 0.7, 0.03, sh, 0.85 * terrainSkyVis(vWorld), 0.85, vec3(0.45, 0.3, 0.2), 0.35);
  fragColor = vec4(col, a.a);
}
`;

const depthFrag = /* glsl */ `
uniform sampler2D uAlbedo;
in vec2 vUv;
layout(location = 0) out vec4 fragColor;
void main() {
  if (texture(uAlbedo, vUv).a < 0.5) discard;
  fragColor = vec4(1.0);
}
`;

export class Impostors implements ShadowCaster {
  readonly mesh: THREE.Mesh;
  private readonly depthScene = new THREE.Scene();
  private readonly uniforms: Record<string, THREE.IUniform>;
  private readonly depthUniforms: Record<string, THREE.IUniform>;
  readonly count: number;

  constructor(layout: ImpostorLayout, albedo: THREE.Texture, normal: THREE.Texture, variantNames: string[],
    records: Float32Array[], heightAt: (x: number, z: number) => number, density: number,
    shadowUniforms: Record<string, THREE.IUniform>, fade: { start: { value: number }; end: { value: number } }, maxDist: number) {
    const V = layout.views;
    const nV = variantNames.length;
    const cells = new Float32Array(V * nV * 4);
    const dims = new Float32Array(nV * 4);
    variantNames.forEach((name, vi) => {
      const t = layout.trees[name];
      if (!t) return;
      for (let k = 0; k < V; k++) cells.set(t.cells[k] ?? t.cells[0], (vi * V + k) * 4);
      dims.set([t.width, t.height, t.baseOffset, 0], vi * 4);
    });
    const cellTex = new THREE.DataTexture(cells, V, nV, THREE.RGBAFormat, THREE.FloatType);
    cellTex.needsUpdate = true;
    const dimTex = new THREE.DataTexture(dims, 1, nV, THREE.RGBAFormat, THREE.FloatType);
    dimTex.needsUpdate = true;

    // instances
    let total = 0;
    for (const r of records) total += r.length / 6;
    const a0 = new Float32Array(total * 4), a1 = new Float32Array(total * 4);
    let k = 0;
    for (const r of records) {
      const n = r.length / 6;
      for (let i = 0; i < n; i++) {
        const v = r[i * 6 + 5];
        const name = variantNames[v];
        if (!layout.trees[name] || /grass/.test(name)) continue;
        if (density < 1 && Math.random() > density) continue;
        const x = r[i * 6], z = r[i * 6 + 2];
        a0.set([x, heightAt(x, z) - 0.1, z, r[i * 6 + 3]], k * 4);
        a1.set([r[i * 6 + 4], v, Math.random(), 0], k * 4);
        k++;
      }
    }
    this.count = k;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    g.setAttribute('aImp0', new THREE.InstancedBufferAttribute(a0.subarray(0, k * 4), 4));
    g.setAttribute('aImp1', new THREE.InstancedBufferAttribute(a1.subarray(0, k * 4), 4));
    g.instanceCount = k;
    const common = {
      uCells: { value: cellTex }, uDims: { value: dimTex }, uViews: { value: V },
      uAlbedo: { value: albedo }, uNormal: { value: normal },
    };
    this.uniforms = {
      ...shared, ...shadowUniforms, ...common,
      uViewPos: { value: new THREE.Vector3() }, uFaceDir: { value: new THREE.Vector3() },
      uMinDist: { value: 0 }, uMaxDist: { value: maxDist },
      uFadeStart: fade.start, uFadeEnd: fade.end,
    };
    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: commonGLSL + curvatureGLSL + vert,
      fragmentShader: commonGLSL + lightingGLSL + shadowGLSL + terrainVisGLSL + frag,
      uniforms: this.uniforms,
      side: THREE.DoubleSide,
      alphaToCoverage: true,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.depthUniforms = {
      ...shared, ...common,
      uViewPos: { value: new THREE.Vector3() }, uFaceDir: { value: new THREE.Vector3() },
      uMinDist: { value: 110 }, uMaxDist: { value: Math.min(maxDist, 6000) },
      uFadeStart: { value: -2 }, uFadeEnd: { value: -1 },
    };
    const dmat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: commonGLSL + curvatureGLSL + vert,
      fragmentShader: depthFrag,
      uniforms: this.depthUniforms,
      side: THREE.DoubleSide,
    });
    const dm = new THREE.Mesh(g, dmat);
    dm.frustumCulled = false;
    this.depthScene.add(dm);
  }

  update(camera: THREE.Camera) {
    this.uniforms.uViewPos.value.copy(camera.position);
    this.depthUniforms.uViewPos.value.copy(camera.position);
  }

  renderShadow(renderer: THREE.WebGLRenderer, cam: THREE.OrthographicCamera, cascade: number) {
    if (cascade < 1) return;
    // billboards face the light so their silhouettes cast plausible shadows
    this.depthUniforms.uFaceDir.value.copy(shared.uSunDir.value);
    renderer.render(this.depthScene, cam);
  }
}
